import { Hand, MousePointer2, Undo2 } from 'lucide-react'
import type React from 'react'

import { cn } from '@modules/lib/lib'
import { Button } from '@modules/shadcn/ui/Button/Button'

import type { CanvasToolMode } from '@ai-color-modules/simulation/hooks/useCanvasToolMode'

interface FloatingToolbarProps {
  /** 現在の実効モード（Space 押下中の一時 pan を含む） */
  mode: CanvasToolMode
  onModeChange: (mode: CanvasToolMode) => void
  /**
   * 「1つ戻す」ボタンのハンドラ。undefined の場合はボタン自体を描画しない
   * (issue #2524: undo を持たない積算 v2 編集キャンバスでの再利用のため generalize)
   */
  onUndo?: () => void
  canUndo?: boolean
  className?: string
}

const itemClass = (selected: boolean) =>
  cn(
    'size-6 rounded p-0',
    selected && 'bg-emerald-500 text-white hover:bg-emerald-500 hover:text-white',
  )

/**
 * ツールチップ付きボタンラッパー。
 * native の title 属性はブラウザ既定の表示遅延 (約 1 秒) を調整できないため、
 * CSS の hover + transition-delay (約 150ms) で高速表示するカスタム実装にしている。
 */
const ToolbarItem: React.FC<{
  label: string
  shortcut: string
  children: React.ReactNode
}> = ({ label, shortcut, children }) => (
  <span className="group relative inline-flex">
    {children}
    <span
      aria-hidden
      className="pointer-events-none absolute right-0 top-full z-20 mt-1.5 flex items-center gap-x-1.5 whitespace-nowrap rounded bg-[#18181B] px-2 py-1 text-xs text-white opacity-0 transition-opacity delay-150 duration-100 group-hover:opacity-100"
    >
      {label}
      <span className="text-zinc-400">{shortcut}</span>
    </span>
  </span>
)

/**
 * エリア編集キャンバス用フローティングツールバー（Figma node-id=13799-14935）。
 *
 * 選択 (V) / キャンバス移動 (Space) / 1つ戻す (⌘Z / Ctrl+Z) の 3 ボタン。
 * キャンバス右上に絶対配置する想定（位置は className で調整）。
 * Undo は押下アクションのみで selected ステートを持たない。
 */
export const FloatingToolbar: React.FC<FloatingToolbarProps> = ({
  mode,
  onModeChange,
  onUndo,
  canUndo,
  className,
}) => {
  return (
    <div
      aria-label="キャンバス操作ツールバー"
      className={cn(
        'flex items-center gap-1 rounded-md border border-input bg-background p-1 shadow-sm',
        className,
      )}
      role="toolbar"
    >
      <ToolbarItem label="選択" shortcut="V">
        <Button
          aria-label="選択"
          aria-pressed={mode === 'select'}
          className={itemClass(mode === 'select')}
          onClick={() => onModeChange('select')}
          size="sm"
          type="button"
          variant="ghost"
        >
          <MousePointer2 size={16} />
        </Button>
      </ToolbarItem>
      <ToolbarItem label="キャンバス移動" shortcut="Space">
        <Button
          aria-label="キャンバス移動"
          aria-pressed={mode === 'pan'}
          className={itemClass(mode === 'pan')}
          onClick={() => onModeChange('pan')}
          size="sm"
          type="button"
          variant="ghost"
        >
          <Hand size={16} />
        </Button>
      </ToolbarItem>
      {onUndo && (
        <ToolbarItem label="1つ戻す" shortcut="⌘Z / Ctrl+Z">
          <Button
            aria-label="1つ戻す"
            className={itemClass(false)}
            disabled={!canUndo}
            onClick={onUndo}
            size="sm"
            type="button"
            variant="ghost"
          >
            <Undo2 size={16} />
          </Button>
        </ToolbarItem>
      )}
    </div>
  )
}
